import React from "react";
import { useTheme } from "styled-components";

import { AVAILABLE_LANGS } from "@typings/globals";

import { CSSGrid } from "@components/Layout/CssGrid";
import {
  BentoBox,
  Props as BentoBoxProps,
} from "@components/DataDisplay/BentoBox";

import { mediaQueriesV2 } from "@ui/themes/default";

import { useTranslate } from "@hooks/useTranslate";

import firstImgMobile from "./assets/mobile/2.1.webp";
import firstImgTablet from "./assets/tablet/2.1.webp";
import firstImgDesktop from "./assets/desktop/2.1.webp";
import thirdImgMobile from "./assets/mobile/2.3.webp";
import thirdImgTablet from "./assets/tablet/2.3.webp";
import thirdImgDesktop from "./assets/desktop/2.3.webp";
import {
  desktopSecondImage,
  mobileSecondImage,
  tabletSecondImage,
} from "./Bento4x4x4.helpers";

const areas = CSSGrid.areas({
  first: BentoBox,
  second: BentoBox,
  third: BentoBox,
});

areas.all(({ first, second, third }, { repeat }) => [
  repeat(first, 12),
  repeat(second, 12),
  repeat(third, 12),
]);

areas.smallTablet(({ first, second, third }, { compose, repeat }) => [
  compose(repeat(first, 4), repeat(second, 4), repeat(third, 4)),
]);

const firstBase64 =
  "data:image/png;base64,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";
const secondBase64 =
  "data:image/png;base64,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";
const thirdBase64 =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABMAAAAgCAYAAADwvkPPAAAAAXNSR0IArs4c6QAABUlJREFUSEudlc1vlNcVxs+97zvzGjz2TGkMY2xgQrBr9d/ognWEmI0lRMWCTYSqbpC66ey6yM6LSGxIhLoaL1GzaSXv2Kbl20wAG3+RgL9mPMzHPc9zqjsJNC6TKMkrXemV7tVPz/Occ891MuRbWFjISqVSEUBRQhgVkRyTxNKUfbNcO5/PH3Q6o2/Gxnq9ixcvBuecRYwbBqvX64k0m8VuPl+Ufn9Uvc8npCXe91yWtVX1gGT78uXLvbegH4XFjVu3bo3mcqHY76cFM8ulKS1V3/Nm7W6StC5dutT+fyFDlcVDA3UiRel0CswsB+TMe98FcDA/P9/6oaK30B+FxQO3b98+GnZ3C+Z9Pk0S9pKku7u727p69WoYFs9PwpaWltK9vb2CWSuf66bspGm3Wq0eDAP9ZGZx08zcvxYXx4NI3ougKdKpVqudXwWLynqvXo1DJJ+mXcqBtM8PCf5nZbZUrxdChnHrJZkmgey5didNd6vVKn5RZgNVO+sTSbBS3yMTEViwdjjqdj7++I97vwj2Zb0+4fXN8b7gN46WEY6a4EBcul1A+s0wu0Or+c96vdjV9qRCy6Y4ZggjpKN5tgD/mom8zI0f36pWq/0fKnwPtvT55yPtvJ3UfndKQ5hU8LcEj1BImrQo/pUXtxkkbK5td17WajUOLUBshcUvPjvhe5wOFk5pCFMK/UDAozAS4ltO+K24dN081kM+3fzkk79sD4X9fWFhPPWhrJBTgd0zVJ7SgAlaGKUNaC16+cZM1sS7F97cRic/9u3169dbh5o23kXd3Pwg+M7Jfg+nSf2Qqqc14ARFRxViYtY0Zy9p/oWkskr4NYwkL0PI79Zqtf67zOIMOyKdCfYwZRoqZDiLoBUFToA6RorR2DSRLXNuleafS5KuiuhW1vM77UKhcwhWCM3jffppaPcsAj4C9EMoJg0Yo5jRrAmRTRFZEeee0qUrYLJRALYPwW7cuJFju33c7M0p6+MjVcwQ4SyBkyDGYikp3Ke5TTF5xiT52js+01y6LnL0da1W675TFiv5xWefnkDPTgN6TlVnAZwFMUVwHEYx2D6d2zCTp+bliRf3lPAvZhqN19XFRRzqs5s3Fyas7c44680AmIXynCJMkSwimqTtQ/y6iH0t5pdVfCMjV/9cq+28N4LqN29OwOwMrDerff0dyXOATpMWH5eobY8ia875hpk8ovknPstWrl271nwP9o96vdwlK0SYJTBHhHMAphUsRlm0AewFKU+cdw/V/OMsy55fuXLlcJ/duXPnSKvVKlsIFVOdDRbmqIx2pwEWSRjMds3Jqpg8hrmHZvYol8s9m5+f332n7MtGIzu2vV0KIZTNLDbrLFTnAJ0BOA3qOMEI2zGTFYg8NvH3xdnDxOzphQsXtgawBw8eHOslyYgAJVGdBFARIGY2p9QZDpRhDBzY3KHZczr3SMzu0bkHItLIm22eP3++7RqrW78XY+bESgE6SQ0VQGep0SZmFDgGQEAKzeJaIeWhOXfXnLuv3ho5kY0DsunWX+/9wegyIUogyoRWwDCLoHNQPTMAvYWRApM3dHLfxO4a/T1x8oTmNtSnTffqoDcfYSZaEmUZ1Ipq7LEQM8ugKmAERlCcaiIUe05x/xaReyZuGd5tCKXp9vv2J3OSibIEWpkY2JyB6pRGEOL6zub/rMo+Rb4yJ3dpbtnEb4hg37XN/moiI0YpglImWEEIEVYYgFRFBzBIvJ8Y5BZ/7Ss6d1fELZtza4Rvuo7Z3wYwkSIQYVKBhhlA0+9A3yuLNr8vAixajbn5/9BsWbysC/3+fwEAJ0Fcv4PVLgAAAABJRU5ErkJggg==";

const firstBox: BentoBoxProps = {
  testId: "bento_v2_first_box",
  variant: "large",
  heading: "bento_v2_first_box_heading",
  description: "bento_v2_first_box_description",
  backgroundColor: "blue300",
};

const secondBox: BentoBoxProps = {
  testId: "bento_v2_second_box",
  variant: "large",
  heading: "bento_v2_second_box_heading",
  description: "bento_v2_second_box_description",
  color: "black",
  backgroundColor: "yellow500",
};

const thirdBox: BentoBoxProps = {
  testId: "bento_v2_third_box",
  variant: "large",
  heading: "bento_v2_third_box_heading",
  description: "bento_v2_third_box_description",
  backgroundColor: "blue600",
};

interface Props {
  language: AVAILABLE_LANGS;
}

export const Bento4x4x4: React.FC<Props> = props => {
  const theme = useTheme();
  const translate = useTranslate("batmaid_pwa_static_homepage");

  return (
    <CSSGrid
      areas={areas}
      rowGap={{
        default: theme.spacing.spacing_24,
        smallDesktop: theme.spacing.spacing_32,
      }}
      areasProps={{
        first: {
          ...firstBox,
          heading: translate(firstBox.heading),
          description:
            typeof firstBox.description === "string"
              ? translate(firstBox.description)
              : firstBox.description,
          children: (
            <BentoBox.Image
              width={816}
              height={1384}
              src={firstImgMobile}
              alt={translate("bento_v2_first_box_img_alt")}
              base64={firstBase64}
              loading="lazy"
              sources={
                <React.Fragment>
                  <source
                    srcSet={firstImgMobile}
                    media={mediaQueriesV2.largeMobile}
                  />
                  <source
                    srcSet={firstImgTablet}
                    media={mediaQueriesV2.smallTablet}
                  />
                  <source
                    srcSet={firstImgTablet}
                    media={mediaQueriesV2.tablet}
                  />
                  <source
                    srcSet={firstImgDesktop}
                    media={mediaQueriesV2.smallDesktop}
                  />
                  <source
                    srcSet={firstImgDesktop}
                    media={mediaQueriesV2.desktop}
                  />
                  <source
                    srcSet={firstImgDesktop}
                    media={mediaQueriesV2.largeDesktop}
                  />
                </React.Fragment>
              }
            />
          ),
        },
        second: {
          ...secondBox,
          heading: translate(secondBox.heading),
          description:
            typeof secondBox.description === "string"
              ? translate(secondBox.description)
              : secondBox.description,
          children: (
            <BentoBox.Image
              width={816}
              height={1384}
              src={mobileSecondImage(props.language)}
              alt={translate("bento_v2_second_box_img_alt")}
              base64={secondBase64}
              loading="lazy"
              sources={
                <React.Fragment>
                  <source
                    srcSet={mobileSecondImage(props.language)}
                    media={mediaQueriesV2.largeMobile}
                  />
                  <source
                    srcSet={tabletSecondImage(props.language)}
                    media={mediaQueriesV2.smallTablet}
                  />
                  <source
                    srcSet={tabletSecondImage(props.language)}
                    media={mediaQueriesV2.tablet}
                  />
                  <source
                    srcSet={desktopSecondImage(props.language)}
                    media={mediaQueriesV2.smallDesktop}
                  />
                  <source
                    srcSet={desktopSecondImage(props.language)}
                    media={mediaQueriesV2.desktop}
                  />
                  <source
                    srcSet={desktopSecondImage(props.language)}
                    media={mediaQueriesV2.largeDesktop}
                  />
                </React.Fragment>
              }
            />
          ),
        },
        third: {
          ...thirdBox,
          heading: translate(thirdBox.heading),
          description:
            typeof thirdBox.description === "string"
              ? translate(thirdBox.description)
              : thirdBox.description,
          children: (
            <BentoBox.Image
              width={948}
              height={1384}
              src={thirdImgMobile}
              alt={translate("bento_v2_third_box_img_alt")}
              base64={thirdBase64}
              loading="lazy"
              sources={
                <React.Fragment>
                  <source
                    srcSet={thirdImgMobile}
                    media={mediaQueriesV2.largeMobile}
                  />
                  <source
                    srcSet={thirdImgTablet}
                    media={mediaQueriesV2.smallTablet}
                  />
                  <source
                    srcSet={thirdImgTablet}
                    media={mediaQueriesV2.tablet}
                  />
                  <source
                    srcSet={thirdImgDesktop}
                    media={mediaQueriesV2.smallDesktop}
                  />
                  <source
                    srcSet={thirdImgDesktop}
                    media={mediaQueriesV2.desktop}
                  />
                  <source
                    srcSet={thirdImgDesktop}
                    media={mediaQueriesV2.largeDesktop}
                  />
                </React.Fragment>
              }
            />
          ),
        },
      }}
    />
  );
};
